import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePlan } from '../hooks/usePlan';
import { PLANBASE_CONFIG } from '../config';
import ChatComposer, { type ChatComposerMode } from '../components/orbi/ChatComposer';
import ChatConversation, { type ChatConversationMessage } from '../components/orbi/ChatConversation';
import PlanverseCard from '../components/orbi/PlanverseCard';
import WorkflowPreflightModal from '../components/orbi/WorkflowPreflightModal';
import OrbiStarterCards, { type StarterPrompt } from '../components/orbi/OrbiStarterCards';
import { isPaidUser } from '../lib/plan';
import { upsertSession } from '../hooks/useOrbiSessions';
import { listPlans, type PlanversePlan } from '../lib/planverse';

type ChatMessage = ChatConversationMessage;

function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 60) return `${Math.max(mins, 1)}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

export default function OrbiAI() {
  const { user } = useAuth();
  const { isPro, planLoading } = usePlan();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedSessionId = searchParams.get('session');

  const [prompt, setPrompt] = useState('');
  const [mode, setMode] = useState<ChatComposerMode | undefined>(undefined);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [planverse, setPlanverse] = useState<PlanversePlan[]>([]);
  // Workflow mode preflight — the user's raw prompt is parked here while
  // the modal collects a couple of context details before we navigate
  // to /orbi/workflow. Empty string means the modal is closed.
  const [preflightPrompt, setPreflightPrompt] = useState('');

  // Read tier from every place it could live (see src/lib/plan.ts).
  // RevenueCat's `isPro` is a mobile-IAP signal only; web-Stripe payers
  // land on the user record instead, so both must be checked.
  const isPaid = isPro || isPaidUser(user);

  const inChat = chatMessages.length > 0 || generating;

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    'X-Project-ID': PLANBASE_CONFIG.projectId,
    'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`,
    'X-User-Token': localStorage.getItem('planbase_token') || '',
  });

  const loadPlanverse = async () => {
    // Reads from the local Planverse mock for now. When Workflow ships a real
    // backend, this will be swapped for a real endpoint call inside listPlans.
    const items = await listPlans();
    setPlanverse(items.slice(0, 4));
  };

  // If the sidebar navigates to a different session (or clears it), reset state.
  useEffect(() => {
    if (!requestedSessionId && sessionId) {
      setSessionId('');
      setChatMessages([]);
      setPrompt('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedSessionId]);

  useEffect(() => {
    // Planverse and session loading run for everyone — free users can
    // browse the empty state, see starter prompts, and read existing
    // conversations even though send() is gated. Keeps parity with the
    // Floating Orbi preview-mode behaviour.
    if (!planLoading) {
      loadPlanverse();
      if (requestedSessionId && requestedSessionId !== sessionId) {
        loadSession(requestedSessionId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planLoading, requestedSessionId]);

  if (planLoading) {
    return <div className="mx-auto mt-12 h-96 max-w-4xl animate-pulse rounded-2xl bg-muted" />;
  }

  const saveSession = async (messages: ChatMessage[]) => {
    try {
      const response = await fetch(
        `${PLANBASE_CONFIG.apiUrl}/sdk/planbase/ai/sessions${sessionId ? `/${sessionId}` : ''}`,
        {
          method: sessionId ? 'PATCH' : 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ messages }),
        },
      );
      const data = await response.json();
      if (!response.ok) return;
      setSessionId(data.session.id);
      setSearchParams({ session: data.session.id }, { replace: true });
      upsertSession(data.session);
    } catch {}
  };

  const loadSession = async (id: string) => {
    setError('');
    try {
      const response = await fetch(
        `${PLANBASE_CONFIG.apiUrl}/sdk/planbase/ai/sessions/${id}`,
        { headers: authHeaders() },
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Unable to load chat');
      const messages: ChatMessage[] = Array.isArray(data.session.messages)
        ? data.session.messages
        : [];
      setSessionId(id);
      setChatMessages(messages);
      setPrompt('');
    } catch (err: any) {
      setError(err.message || 'Unable to load chat');
    }
  };

  const submit = async () => {
    const value = prompt.trim();
    if (!value) return;

    // Workflow mode → ask a few targeted questions first, then hand the
    // enriched brief to the canvas. Skip in the modal preserves the
    // original prompt path.
    if (mode === 'workflow') {
      setPreflightPrompt(value);
      return;
    }

    // Optimistic user message so the layout swaps immediately.
    const pendingMessages: ChatMessage[] = [
      ...chatMessages,
      { role: 'user', content: value },
    ];
    setChatMessages(pendingMessages);
    setPrompt('');
    setGenerating(true);
    setError('');

    try {
      const action =
        mode === 'portfolio'
          ? 'portfolio_brief'
          : mode === 'research'
            ? 'research'
            : value.toLowerCase().includes('floor')
              ? 'floor_plan_brief'
              : 'project_brief';
      const response = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/planbase/ai`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ action, prompt: value, mode, useArchitectData: true }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Unable to generate a brief');
      const assistantMessage = data.result || 'No result was returned.';
      const nextMessages: ChatMessage[] = [
        ...pendingMessages,
        { role: 'assistant', content: assistantMessage },
      ];
      setChatMessages(nextMessages);
      await saveSession(nextMessages);
    } catch (err: any) {
      setError(err.message || 'Unable to generate a brief');
      // Roll back the optimistic user message on failure so nothing looks half-sent.
      setChatMessages(chatMessages);
      setPrompt(value);
    } finally {
      setGenerating(false);
    }
  };

  // Shared preflight — Workflow mode routes through this before opening the canvas.
  const preflight = (
    <WorkflowPreflightModal
      open={!!preflightPrompt}
      originalPrompt={preflightPrompt}
      onCancel={() => setPreflightPrompt('')}
      onSkip={() => {
        navigate(`/orbi/workflow?prompt=${encodeURIComponent(preflightPrompt)}`);
        setPreflightPrompt('');
        setPrompt('');
      }}
      onConfirm={(enriched) => {
        navigate(`/orbi/workflow?prompt=${encodeURIComponent(enriched)}`);
        setPreflightPrompt('');
        setPrompt('');
      }}
    />
  );

  // Active-chat layout: conversation fills the middle, composer docks to the bottom.
  if (inChat) {
    return (
      <>
        <div className="flex h-[calc(100vh-2rem)] w-full flex-col md:h-screen">
          <div className="flex-1 overflow-y-auto px-4 py-6 md:py-10">
            <ChatConversation messages={chatMessages} loading={generating} mode={mode} />
          </div>
          <div className="border-t border-border bg-background px-4 py-4">
            <div className="mx-auto w-full max-w-3xl">
              <ChatComposer
                value={prompt}
                onChange={setPrompt}
                onSubmit={submit}
                mode={mode}
                onModeChange={setMode}
                disabled={generating}
                locked={!isPaid && !planLoading}
                onUpgrade={() => navigate('/pricing')}
              />
              {error && (
                <p role="alert" className="mt-2 text-center text-sm text-destructive">
                  {error}
                </p>
              )}
            </div>
          </div>
        </div>
        {preflight}
      </>
    );
  }

  // Empty state: page header + hero + centered composer + Planverse.
  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-16 px-6 py-8 font-display md:px-10 md:py-12">
      {/* Small page label at top-left, matching the app-wide header style. */}
      <div className="flex items-center justify-between">
        <h1 className="text-sm font-semibold uppercase tracking-wide text-foreground">
          Orbi AI
        </h1>
      </div>

      <div className="flex w-full flex-col items-center gap-8 pt-8 md:pt-12">
        <p className="text-center text-4xl leading-tight text-foreground md:text-6xl">
          What are we doing today?
        </p>

        <ChatComposer
          value={prompt}
          onChange={setPrompt}
          onSubmit={submit}
          mode={mode}
          onModeChange={setMode}
          disabled={generating}
          locked={!isPaid && !planLoading}
          onUpgrade={() => navigate('/pricing')}
        />

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        {/*
          Starter prompts — see A-000 finding K.
          Prefills the composer and picks the matching mode so a user
          can click, edit a word, and send. Highest-leverage discoverability
          fix on the app.
        */}
        <OrbiStarterCards
          className="mt-2"
          onPick={(p: StarterPrompt) => {
            setMode(p.mode);
            setPrompt(p.prompt);
          }}
        />
      </div>

      <section className="flex w-full flex-col gap-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl text-foreground">Planverse</h2>
          <button
            type="button"
            onClick={() => navigate('/planverse')}
            className="text-base text-primary hover:opacity-80"
          >
            See More
          </button>
        </div>

        {planverse.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Loading Planverse…
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {planverse.map((item) => (
              <PlanverseCard
                key={item.id}
                title={item.title}
                imageUrl={item.coverUrl}
                timestamp={timeAgo(item.publishedAt)}
                authorInitial={item.author.initial}
                likes={item.likes}
                duplicates={item.duplicates}
                views={item.views}
                onClick={() => navigate(`/planverse/${item.id}`)}
              />
            ))}
          </div>
        )}
      </section>
      {preflight}
    </div>
  );
}
